import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/*
 * Renders a page of the original childrensparliament.uk site, like-for-like.
 * Markup lives in /public/mirror/pages/*.html (built by /app/scripts/build_mirror.py),
 * styled by the original theme stylesheet and brought to life by the original theme
 * script (jQuery + main.js: dropdown menus, mobile menu, Fancybox video lightbox,
 * masonry cards, AOS animations, scroll buttons, parallax shapes, form selector).
 * Gravity Forms are submitted to our own backend (/api/form-submission).
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const JQUERY = "https://ajax.googleapis.com/ajax/libs/jquery/3.5.1/jquery.min.js";
const THEME_JS = "/mirror/js/main.js";

let manifestPromise;
const getManifest = () => {
  if (!manifestPromise) manifestPromise = fetch("/mirror/manifest.json").then((r) => r.json());
  return manifestPromise;
};

const loadScript = (src) =>
  new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = src;
    s.async = false;
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });

const fieldLabel = (field) => {
  const label = field.querySelector(".gfield_label, legend");
  if (!label) return field.id || "field";
  const clone = label.cloneNode(true);
  clone.querySelectorAll(".gfield_required").forEach((n) => n.remove());
  return clone.textContent.trim();
};

const fieldValue = (field) => {
  const inputs = [...field.querySelectorAll("input, select, textarea")].filter((i) => i.type !== "hidden");
  const checks = inputs.filter((i) => i.type === "checkbox" || i.type === "radio");
  if (checks.length) return checks.filter((i) => i.checked).map((i) => i.value).join(", ");
  return inputs.map((i) => i.value.trim()).filter(Boolean).join(" ");
};

const clearErrors = (form) => {
  form.querySelectorAll(".gform_validation_errors, .cp-validation-message").forEach((n) => n.remove());
  form.querySelectorAll(".gfield_error").forEach((f) => f.classList.remove("gfield_error"));
};

// Media from the old WordPress library is self-hosted under /public/wp-content (recovered by
// /app/scripts/recover_wp_assets.py). A few files could not be recovered: if an image (or one of
// its srcset sizes) is missing, fall back to the plain src, then hide it rather than showing a
// broken-image icon. Re-uploading the file to the same /wp-content/... path makes it reappear.
const hideMissingMedia = (root) => {
  root.querySelectorAll("img").forEach((img) => {
    const onError = () => {
      if (img.hasAttribute("srcset")) {
        img.removeAttribute("srcset");
        img.removeAttribute("sizes");
        return;
      }
      img.removeEventListener("error", onError);
      img.style.display = "none";
      const figure = img.closest("figure");
      if (figure && figure.querySelectorAll("img").length === 1) figure.style.display = "none";
    };
    img.addEventListener("error", onError);
  });
  root.querySelectorAll("audio, video").forEach((media) => {
    const hide = () => {
      const figure = media.closest("figure");
      (figure || media).style.display = "none";
    };
    media.addEventListener("error", hide);
    media.querySelectorAll("source").forEach((s) => s.addEventListener("error", hide));
  });
  // Card / content photos are CSS backgrounds: if one is missing, collapse its empty photo area.
  root.querySelectorAll(".img-wrap [style*='background-image'], .box-content .box-wrapper > .bg-img[style*='background-image']").forEach((el) => {
    const m = (el.getAttribute("style") || "").match(/url\(\s*['"]?([^'")]+)['"]?\s*\)/);
    if (!m) return;
    const probe = new Image();
    probe.onerror = () => {
      const wrap = el.closest(".img-wrap") || el;
      if (wrap) wrap.style.display = "none";
      const $ = window.jQuery;
      if ($ && $.fn && $.fn.masonry) $(".box-grid").each((_, g) => $(g).data("masonry") && $(g).masonry("layout"));
    };
    probe.src = m[1];
  });
};

const wireForms = (root, page) => {
  root.querySelectorAll("form[id^='gform_']").forEach((form) => {
    const wrapper = form.closest(".gform_wrapper") || form;
    const formId = form.getAttribute("data-formid") || form.id.replace("gform_", "");
    const title = wrapper.querySelector(".gform_title")?.textContent.trim() || "";
    let sending = false;

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      if (sending) return;
      clearErrors(form);

      const fields = {};
      let firstError = null;
      form.querySelectorAll(".gfield").forEach((field) => {
        if (field.classList.contains("gfield_visibility_hidden")) return;
        const value = fieldValue(field);
        const required = field.classList.contains("gfield_contains_required");
        const isEmail = field.classList.contains("gfield--type-email");
        let msg = null;
        if (required && !value) msg = "This field is required.";
        else if (isEmail && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) msg = "The email address entered is invalid, please check the formatting (e.g. email@domain.com).";
        if (msg) {
          field.classList.add("gfield_error");
          const div = document.createElement("div");
          div.className = "gfield_description validation_message gfield_validation_message cp-validation-message";
          div.textContent = msg;
          field.appendChild(div);
          firstError = firstError || field;
        }
        fields[fieldLabel(field)] = value;
      });

      if (firstError) {
        const summary = document.createElement("div");
        summary.className = "gform_validation_errors";
        summary.innerHTML = '<h2 class="gform_submission_error">There was a problem with your submission. Please review the fields below.</h2>';
        form.prepend(summary);
        firstError.querySelector("input, select, textarea")?.focus();
        return;
      }

      const button = form.querySelector("[type='submit']");
      sending = true;
      if (button) button.disabled = true;
      try {
        const res = await fetch(`${API}/form-submission`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ form_id: formId, form_title: title, page, fields }),
        });
        if (!res.ok) throw new Error(String(res.status));
        const data = await res.json();
        const confirmation = document.createElement("div");
        confirmation.className = "gform_confirmation_wrapper";
        confirmation.setAttribute("data-testid", `form-${formId}-confirmation`);
        confirmation.innerHTML = '<div class="gform_confirmation_message"></div>';
        confirmation.firstChild.textContent = data.message;
        wrapper.replaceWith(confirmation);
      } catch (err) {
        const summary = document.createElement("div");
        summary.className = "gform_validation_errors";
        summary.innerHTML = '<h2 class="gform_submission_error">Something went wrong. Please try again.</h2>';
        form.prepend(summary);
        sending = false;
        if (button) button.disabled = false;
      }
    });
  });
};

export default function MirrorPage() {
  const { pathname, hash } = useLocation();
  const ref = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const manifest = await getManifest();
      let path = "/";
      try {
        path = decodeURI(pathname).replace(/\/+$/, "") || "/";
      } catch (e) {
        path = pathname;
      }
      const entry = manifest[path] || manifest.__404__;
      const html = await fetch(`/mirror/pages/${entry.file}`).then((r) => r.text());
      if (cancelled || !ref.current) return;

      document.title = entry.title;
      document.body.className = entry.bodyClass;
      ref.current.innerHTML = html;
      hideMissingMedia(ref.current);
      wireForms(ref.current, path);

      // Original theme behaviour (runs once per page load; internal links are full page loads like the original).
      if (!window.jQuery) await loadScript(JQUERY);
      if (!window.__cpThemeLoaded) {
        window.__cpThemeLoaded = true;
        await loadScript(THEME_JS);
      }
      if (hash) {
        const target = document.getElementById(hash.slice(1));
        if (target) target.scrollIntoView();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pathname, hash]);

  return <div ref={ref} data-testid="mirror-page" />;
}
